import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    IconButton,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import RequestService from '../../services/RequestService';

const NewProductDialog = ({ open, onClose, onSuccess, user }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: 0,
        initialQuantity: '',
        entireGame: true,
        games: [],
        photo: null,
        fulfillmentType: 'PHYSICAL',
        contentFile: null,
        notify: false,
    });
    const [error, setError] = useState(null);
    const [games, setGames] = useState([]);
    const [gameSelection, setGameSelection] = useState('Todos');

    useEffect(() => {
        if (gameSelection === 'Alguns') {
            const fetchGames = async () => {
                try {
                    const gamesData = await RequestService.getGames(
                        user.company.id
                    );
                    setGames(Array.isArray(gamesData) ? gamesData : []);
                } catch (err) {
                    setGames([]);
                    console.error('Erro ao carregar jogos:', err);
                }
            };
            fetchGames();
        }
    }, [gameSelection, user.company.id]);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'file' ? files[0] : value,
        }));
    };

    const handleGameSelection = (event, newSelection) => {
        if (newSelection) {
            setGameSelection(newSelection);
            setFormData((prev) => ({
                ...prev,
                entireGame: newSelection === 'Todos',
                games: newSelection === 'Todos' ? [] : prev.games,
            }));
        }
    };

    const handleGameToggle = (gameId) => {
        setFormData((prev) => {
            const isSelected = prev.games.includes(gameId);
            const newGames = isSelected
                ? prev.games.filter((id) => id !== gameId)
                : [...prev.games, gameId];
            return { ...prev, games: newGames };
        });
    };

    const handleSubmit = async () => {
        try {
            if (
                formData.fulfillmentType === 'DIGITAL' &&
                (!formData.contentFile ||
                    formData.contentFile.type !== 'application/pdf' ||
                    formData.contentFile.size > 20 * 1024 * 1024)
            ) {
                setError('Selecione um arquivo PDF de até 20 MB.');
                return;
            }
            const formDataToSend = new FormData();

            formDataToSend.append('name', formData.name);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('price', formData.price);
            formDataToSend.append('fulfillmentType', formData.fulfillmentType);
            if (formData.fulfillmentType === 'PHYSICAL') {
                formDataToSend.append(
                    'initialQuantity',
                    formData.initialQuantity
                );
            } else {
                formDataToSend.append('contentFile', formData.contentFile);
            }
            formDataToSend.append('companyId', user.company.id);

            if (gameSelection === 'Todos') {
                formDataToSend.append('entireGame', true);
            } else {
                formDataToSend.append('entireGame', false);
                formData.games.forEach((gameId) =>
                    formDataToSend.append('gameIds', gameId)
                );
            }

            if (formData.photo) {
                formDataToSend.append('photo', formData.photo);
            }

            await RequestService.createProduct(formDataToSend);

            setFormData({
                name: '',
                description: '',
                price: 0,
                initialQuantity: '',
                games: [],
                photo: null,
                fulfillmentType: 'PHYSICAL',
                contentFile: null,
                notify: false,
            });

            onSuccess('Produto criado com sucesso');
            onClose();
        } catch (err) {
            setError('Erro ao criar o produto. Verifique os dados.');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                Nova Recompensa
                <IconButton
                    aria-label='close'
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            backgroundColor: '#F5F5F5',
                            padding: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Box
                            sx={{
                                width: 100,
                                height: 100,
                                border: '1px dashed #CCCCCC',
                                borderRadius: '50%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                cursor: 'pointer',
                                overflow: 'hidden',
                            }}
                        >
                            {formData.photo ? (
                                <img
                                    src={URL.createObjectURL(formData.photo)}
                                    alt='Preview'
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : (
                                <AddPhotoAlternateOutlinedIcon
                                    fontSize='large'
                                    color='disabled'
                                />
                            )}
                        </Box>
                        <Button variant='contained' component='label' fullWidth>
                            Selecionar Imagem
                            <input
                                type='file'
                                accept='image/*'
                                hidden
                                name='photo'
                                onChange={handleChange}
                            />
                        </Button>
                    </Box>

                    <TextField
                        label='Título'
                        name='name'
                        value={formData.name}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        label='Descrição'
                        name='description'
                        value={formData.description}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={3}
                    />
                    <Typography>Tipo de recompensa</Typography>
                    <Alert severity='info'>
                        <Typography variant='body2'>
                            <strong>Produto físico:</strong> recompensa com
                            estoque que o jogador precisa retirar
                            presencialmente. Exemplos: balas, canecas etc.
                        </Typography>
                        <Typography variant='body2' sx={{ mt: 1 }}>
                            <strong>Conteúdo digital:</strong> arquivo PDF
                            liberado imediatamente após o resgate e disponível
                            no Histórico do jogador. Exemplos: resumo de livro,
                            apostila ou material de apoio.
                        </Typography>
                    </Alert>
                    <ToggleButtonGroup
                        value={formData.fulfillmentType}
                        exclusive
                        fullWidth
                        onChange={(_, value) =>
                            value &&
                            setFormData((prev) => ({
                                ...prev,
                                fulfillmentType: value,
                                initialQuantity:
                                    value === 'DIGITAL'
                                        ? ''
                                        : prev.initialQuantity,
                                contentFile:
                                    value === 'PHYSICAL'
                                        ? null
                                        : prev.contentFile,
                            }))
                        }
                    >
                        <ToggleButton value='PHYSICAL'>
                            Produto físico
                        </ToggleButton>
                        <ToggleButton value='DIGITAL'>
                            Conteúdo digital
                        </ToggleButton>
                    </ToggleButtonGroup>
                    {formData.fulfillmentType === 'DIGITAL' && (
                        <Box>
                            <Button
                                variant='outlined'
                                component='label'
                                fullWidth
                            >
                                {formData.contentFile?.name || 'Selecionar PDF'}
                                <input
                                    type='file'
                                    accept='application/pdf,.pdf'
                                    hidden
                                    onChange={(event) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            contentFile:
                                                event.target.files?.[0] || null,
                                        }))
                                    }
                                />
                            </Button>
                            <Typography variant='caption'>
                                PDF de até 20 MB
                            </Typography>
                        </Box>
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <TextField
                            label='Custo'
                            name='price'
                            type='number'
                            value={formData.price}
                            onChange={handleChange}
                            InputProps={{
                                startAdornment: <CoinIcon />,
                            }}
                            fullWidth
                        />
                        {formData.fulfillmentType === 'PHYSICAL' && (
                            <TextField
                                label='Quantidade'
                                name='initialQuantity'
                                type='number'
                                value={formData.initialQuantity}
                                onChange={handleChange}
                                fullWidth
                            />
                        )}
                    </Box>

                    <Typography>Jogos</Typography>
                    <ToggleButtonGroup
                        value={gameSelection}
                        exclusive
                        onChange={handleGameSelection}
                        fullWidth
                    >
                        <ToggleButton value='Todos'>Todos</ToggleButton>
                        <ToggleButton value='Alguns'>Alguns</ToggleButton>
                    </ToggleButtonGroup>
                    {gameSelection === 'Alguns' &&
                        (games.length === 0 ? (
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ mt: 1 }}
                            >
                                Nenhum jogo disponível.
                            </Typography>
                        ) : (
                            <List>
                                {games.map((game) => (
                                    <ListItem
                                        key={game.id}
                                        dense
                                        button
                                        onClick={() =>
                                            handleGameToggle(game.id)
                                        }
                                    >
                                        <ListItemIcon>
                                            <Checkbox
                                                edge='start'
                                                checked={formData.games.includes(
                                                    game.id
                                                )}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                        </ListItemIcon>
                                        <ListItemText primary={game.name} />
                                    </ListItem>
                                ))}
                            </List>
                        ))}

                    {error && <Alert severity='error'>{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant='outlined'>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    color='primary'
                    variant='contained'
                >
                    Criar Recompensa
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewProductDialog;
